import axiosClient, { type AxiosError, type AxiosRequestConfig, type AxiosInstance } from 'axios'
import merge from 'lodash/merge'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { Logger } from '@egw/logger'

const log = new Logger('HTTP Client')

type PathParams = Record<string, string | number>

declare module 'axios' {
  export interface AxiosRequestConfig {
    pathParams?: PathParams
  }
}

interface Config {
  url: string
  accessToken: string
}

interface HandleSuccessProps {
  data: unknown
}

const defaultConfig = {
  headers: {
    Accept: 'application/json',
  },
  withCredentials: true,
}

const generateConfig = (config?: AxiosRequestConfig): AxiosRequestConfig => {
  if (!config?.headers?.['Content-Type']) {
    const defConfig = { ...defaultConfig, 'Content-Type': 'application/json' }
    return merge(config, defConfig) as AxiosRequestConfig
  }
  return merge(config, defaultConfig) as AxiosRequestConfig
}

const handleSuccess = ({ data }: HandleSuccessProps): unknown => {
  const responseData = data as { data: unknown }
  return responseData.data ? responseData.data : responseData
}

/**
 * HTTP error type
 */
export interface HttpError {
  code: number
  message: string
  timestamp?: string
}

const handleErrors = (err: unknown): never => {
  const error = err as AxiosError
  if (error.response?.data) {
    const apiError = error.response.data as HttpError
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw apiError
  }
  const apiError: HttpError = {
    code: error.response?.status ? error.response.status : 1000,
    message: error.message,
  }
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw apiError
}

/**
 * Singleton class for the http client.
 */
class HttpSingleton {
  private client: AxiosInstance = axiosClient.create()
  private accessToken: string | undefined

  constructor() {
    if (!instance) {
      instance = this
    }

    // Set up request interceptor to dynamically add Authorization header
    this.client.interceptors.request.use(
      (config) => {
        if (this.accessToken) {
          config.headers.Authorization = this.accessToken.startsWith('Bearer ')
            ? this.accessToken
            : `Bearer ${this.accessToken}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error instanceof Error ? error : new Error(String(error)))
      },
    )

    const pingSessionListener = (data: unknown): void => {
      const eventData = data as { detail: unknown }
      log.info('Pinging session', eventData.detail)
      const accessToken = (eventData.detail as { accessToken?: string }).accessToken
      if (accessToken) {
        // Store token without Bearer prefix - interceptor will add it if needed
        this.accessToken = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`
        // Also update defaults for backward compatibility
        this.client.defaults.headers.common.Authorization = this.accessToken
        log.info('Access token updated in HTTP client')
      }
    }
    notifications.on(NOTIFICATIONS.AUTH.SESSION_LOADED, pingSessionListener)

    // eslint-disable-next-line no-constructor-return
    return instance
  }

  init(config: Config): void {
    this.client.defaults.baseURL = config.url
    this.client.defaults.headers.common.Authorization = config.accessToken
    log.info('Client is initialized')
  }

  /**
   * Async method to send a GET http request to a remote server
   * @param url - remote server url
   * @param config - request config
   */
  async get(url: string, config?: AxiosRequestConfig): Promise<unknown> {
    return this.client.get(url, generateConfig(config)).then(handleSuccess).catch(handleErrors)
  }

  /**
   * Async method to send a POST http request to a remote server
   * @param url - remote server url
   * @param body - request body
   * @param config - request config
   */
  async post(url: string, body: unknown, config?: AxiosRequestConfig): Promise<unknown> {
    return this.client.post(url, body, generateConfig(config)).then(handleSuccess).catch(handleErrors)
  }

  /**
   * Async method to send a PUT http request to a remote server
   * @param url - remote server url
   * @param body - request body
   * @param config - request config
   */
  async put(url: string, body: unknown, config?: AxiosRequestConfig): Promise<unknown> {
    return this.client.put(url, body, generateConfig(config)).then(handleSuccess).catch(handleErrors)
  }

  /**
   * Async method to send a PATCH http request to a remote server
   * @param url - remote server url
   * @param body - request body
   * @param config - request config
   */
  async patch(url: string, body: unknown, config?: AxiosRequestConfig): Promise<unknown> {
    return this.client.patch(url, body, generateConfig(config)).then(handleSuccess).catch(handleErrors)
  }

  /**
   * Async method to send a DELETE http request to a remote server
   * @param url - remote server url
   * @param config - request config
   */
  async del(url: string, config?: AxiosRequestConfig): Promise<unknown> {
    return this.client.delete(url, generateConfig(config)).then(handleSuccess).catch(handleErrors)
  }
}

let instance: HttpSingleton | null = null

export const http = new HttpSingleton()
